import React, { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BarChartIcon from "@mui/icons-material/BarChart";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import PieChartIcon from "@mui/icons-material/PieChart";
import PrintIcon from "@mui/icons-material/Print";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HowToVoteOutlinedIcon from "@mui/icons-material/HowToVoteOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import {
    IChannel,
    IVisualizationChart,
    ITotalNumbersStats,
    SampleConfidence,
} from "@Interfaces/Entities/Channels";
import {
    ChannelActions,
    NotificationActions,
    PollActions,
    useAppDispatch,
} from "@Store";
import { PDFDocument } from "pdf-lib";
import html2canvas from "html2canvas";

const NAVY = "#1a2744";
const INDIGO = "#4f5de4";
const MUTED = "#64748b";
const BORDER = "#e8eaf6";
const GRADIENT_HERO =
    "linear-gradient(135deg, #1a2744 0%, #2d3f7a 60%, #4c5fa8 100%)";
const CONFIDENCE_STYLES: Record<
    SampleConfidence,
    { color: string; bgcolor: string }
> = {
    low: { color: "#b45309", bgcolor: "#fef3c7" },
    mid: { color: "#b45309", bgcolor: "#fef3c7" },
    good: { color: INDIGO, bgcolor: "#e6e8fc" },
    excellent: { color: "#1d7a4c", bgcolor: "#e0f5e9" },
};
const REPORT_POLL_INTERVAL_MS = 8000;
const PDF_PAGE_WIDTH = 612; // Letter width in points
const PDF_PAGE_HEIGHT = 792; // Letter height in points
const PDF_CAPTURE_SCALE = 2;
const MIN_PAGE_FILL_RATIO = 0.6;
const FALLBACK_BREAK_MIN_FILL_RATIO = 0.6;
const MIN_TRAILING_PAGE_FILL_RATIO = 0.35;
const PROTECTED_RANGE_PADDING_CSS_PX = 8;
const PROTECTED_BREAK_TOLERANCE_PX = 20;
const Lottie = React.lazy(() => import("lottie-react"));

const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
};

const sanitizeFileName = (value: string) =>
    value.replace(/[^a-z0-9]/gi, "_").toLowerCase();

const collectSectionBreakpoints = (
    container: HTMLElement,
    scale: number
): number[] => {
    const containerRect = container.getBoundingClientRect();
    const nodes = Array.from(
        container.querySelectorAll<HTMLElement>("[data-pdf-section='true']")
    );

    return nodes
        .map((node) => {
            const nodeRect = node.getBoundingClientRect();
            const offsetInCssPx = nodeRect.top - containerRect.top;
            return Math.max(0, Math.round(offsetInCssPx * scale));
        })
        .filter((offset) => offset > 0)
        .filter((offset, index, list) => list.indexOf(offset) === index)
        .sort((a, b) => a - b);
};

const getLatestBreakpointInRange = (
    breakpoints: number[],
    minExclusive: number,
    maxInclusive: number,
    minRequiredInclusive: number
): number | null => {
    let latestBreakpoint: number | null = null;

    for (const breakpoint of breakpoints) {
        if (breakpoint <= minExclusive) continue;
        if (breakpoint > maxInclusive) continue;
        if (breakpoint < minRequiredInclusive) continue;
        latestBreakpoint = breakpoint;
    }

    return latestBreakpoint;
};

type ProtectedRange = { start: number; end: number };

const collectProtectedRanges = (
    container: HTMLElement,
    scale: number
): ProtectedRange[] => {
    const containerRect = container.getBoundingClientRect();
    const nodes = Array.from(
        container.querySelectorAll<HTMLElement>("[data-pdf-protect='true']")
    );

    return nodes
        .map((node) => {
            const nodeRect = node.getBoundingClientRect();
            const startInCssPx =
                nodeRect.top -
                containerRect.top -
                PROTECTED_RANGE_PADDING_CSS_PX;
            const endInCssPx =
                nodeRect.bottom -
                containerRect.top +
                PROTECTED_RANGE_PADDING_CSS_PX;
            return {
                start: Math.max(0, Math.round(startInCssPx * scale)),
                end: Math.max(0, Math.round(endInCssPx * scale)),
            };
        })
        .filter((range) => range.end > range.start)
        .sort((a, b) => a.start - b.start);
};

const ReportSectionTitleSx = {
    fontWeight: 700,
    color: NAVY,
    textAlign: "left" as const,
    lineHeight: 1.5,
    fontSize: "1.68rem",
};

interface ReportHeroProps {
    generatedAt?: string | null;
    downloading: boolean;
    onDownloadCsv: () => void;
    onDownloadJson: () => void;
    onPrint: () => void;
}

const ReportHero: React.FC<ReportHeroProps> = ({
    generatedAt,
    downloading,
    onDownloadCsv,
    onDownloadJson,
    onPrint,
}) => {
    const { t, i18n } = useTranslation();
    const generatedDate = generatedAt
        ? new Date(generatedAt).toLocaleDateString(i18n.language || undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
          })
        : null;

    return (
        <Paper
            data-pdf-section="true"
            elevation={0}
            sx={{
                minHeight: 50,
                px: { xs: 1.5, md: 2 },
                py: 0.75,
                mb: 3,
                borderRadius: 2,
                background: GRADIENT_HERO,
                color: "#fff",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1.5}
                sx={{ width: "100%" }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.25}
                    sx={{ minWidth: 0, position: "relative", zIndex: 1 }}
                >
                    <Chip
                        icon={
                            <AutoAwesomeIcon
                                sx={{ fontSize: "14px !important" }}
                            />
                        }
                        label={t("pages.eventInsights.report.badge")}
                        size="small"
                        sx={{
                            height: 24,
                            bgcolor: "rgba(255,255,255,0.15)",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            border: "1px solid rgba(255,255,255,0.25)",
                            backdropFilter: "blur(4px)",
                            flexShrink: 0,
                            "& .MuiChip-icon": { color: "#f5c842" },
                        }}
                    />

                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ flexShrink: 0 }}
                    >
                        <CalendarTodayIcon
                            sx={{ fontSize: 13, opacity: 0.65 }}
                        />
                        <Typography
                            variant="caption"
                            noWrap
                            sx={{ opacity: 0.65 }}
                        >
                            {generatedDate
                                ? t("pages.eventInsights.report.generatedOn", {
                                      date: generatedDate,
                                  })
                                : t(
                                      "pages.eventInsights.report.generatedUnknown"
                                  )}
                        </Typography>
                    </Stack>
                </Stack>

                <Stack
                    direction="row"
                    spacing={0.75}
                    flexWrap="nowrap"
                    alignItems="center"
                    className="no-print"
                    data-html2canvas-ignore="true"
                    sx={{ position: "relative", zIndex: 1, flexShrink: 0 }}
                >
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={onDownloadCsv}
                        disabled={downloading}
                        sx={{
                            minHeight: 28,
                            px: 1,
                            color: "#fff",
                            borderColor: "rgba(255,255,255,0.35)",
                            "&:hover": {
                                borderColor: "#fff",
                                bgcolor: "rgba(255,255,255,0.1)",
                            },
                        }}
                    >
                        {t("pages.eventInsights.report.downloadCsv")}
                    </Button>

                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={onDownloadJson}
                        disabled={downloading}
                        sx={{
                            minHeight: 28,
                            px: 1,
                            color: "#fff",
                            borderColor: "rgba(255,255,255,0.35)",
                            "&:hover": {
                                borderColor: "#fff",
                                bgcolor: "rgba(255,255,255,0.1)",
                            },
                        }}
                    >
                        {t("pages.eventInsights.report.downloadJson")}
                    </Button>

                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<PrintIcon />}
                        onClick={onPrint}
                        disabled={downloading}
                        sx={{
                            minHeight: 28,
                            px: 1.25,
                            bgcolor: "#fff",
                            color: NAVY,
                            fontWeight: 700,
                            boxShadow: "none",
                            "&:hover": {
                                bgcolor: "rgba(255,255,255,0.9)",
                                boxShadow: "none",
                            },
                        }}
                    >
                        {t("pages.eventInsights.report.exportPdf")}
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    );
};

interface StatTileProps {
    icon: React.ReactNode;
    value: string;
    label: string;
    footer?: React.ReactNode;
}

const StatTile: React.FC<StatTileProps> = ({ icon, value, label, footer }) => (
    <Paper
        data-pdf-section="true"
        elevation={0}
        role="group"
        aria-label={`${label}: ${value}`}
        sx={{
            minWidth: 0,
            p: { xs: 1.6, sm: 2 },
            borderRadius: 3,
            border: `1px solid ${BORDER}`,
            bgcolor: "#fff",
            transition: "box-shadow 0.2s ease, border-color 0.2s ease",
            "&:hover": {
                borderColor: "#d7dcfa",
                boxShadow: "0 4px 16px rgba(26,39,68,0.06)",
            },
        }}
    >
        <Box
            aria-hidden="true"
            sx={{
                width: 29,
                height: 29,
                borderRadius: "9px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#edeffc",
                color: INDIGO,
                mb: 1.2,
                "& svg": { fontSize: 16 },
            }}
        >
            {icon}
        </Box>

        <Typography
            component="div"
            aria-hidden="true"
            sx={{
                fontSize: { xs: "1.52rem", sm: "1.8rem" },
                fontWeight: 800,
                color: NAVY,
                lineHeight: 1.1,
            }}
        >
            {value}
        </Typography>

        <Typography
            aria-hidden="true"
            variant="body2"
            sx={{ color: MUTED, mt: 0.6 }}
        >
            {label}
        </Typography>

        {footer}
    </Paper>
);

const SampleConfidenceChip: React.FC<{ tier: SampleConfidence }> = ({
    tier,
}) => {
    const { t } = useTranslation();
    const style = CONFIDENCE_STYLES[tier];

    if (!style) return null;

    const { color, bgcolor } = style;

    return (
        <Chip
            size="small"
            label={t(
                `pages.eventInsights.report.totalNumbers.confidence.${tier}`
            )}
            sx={{
                mt: 1.25,
                height: 22,
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "0.03em",
                color,
                bgcolor,
                "& .MuiChip-label": { px: 1 },
            }}
        />
    );
};

const EngagementOptionBar: React.FC<{
    label: string;
    percentage: number;
    count?: number;
}> = ({ label, percentage, count }) => {
    const clampedPct = Math.min(Math.max(percentage, 0), 100);

    return (
        <Box
            role="group"
            aria-label={`${label}: ${percentage}%`}
            sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 38,
                px: 1.5,
                borderRadius: "10px",
                bgcolor: "#f1f3fa",
                overflow: "hidden",
            }}
        >
            <Box
                aria-hidden="true"
                sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: `${clampedPct}%`,
                    bgcolor: "#e1e4fa",
                    transition: "width 0.4s ease",
                }}
            />
            <Typography
                variant="body2"
                sx={{
                    position: "relative",
                    zIndex: 1,
                    color: NAVY,
                    fontWeight: 500,
                    pr: 1.5,
                }}
            >
                {label}
                {typeof count === "number" ? ` (${count})` : ""}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    position: "relative",
                    zIndex: 1,
                    color: NAVY,
                    fontWeight: 700,
                    flexShrink: 0,
                }}
            >
                {percentage}%
            </Typography>
        </Box>
    );
};

const DoubleVotePollRow: React.FC<{ question: string; count: number }> = ({
    question,
    count,
}) => (
    <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{
            px: 1.5,
            py: 1,
            borderRadius: "10px",
            bgcolor: "#f1f3fa",
        }}
    >
        <Tooltip title={question} arrow placement="top">
            <Typography
                variant="body2"
                sx={{
                    color: NAVY,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {question}
            </Typography>
        </Tooltip>
        <Typography
            variant="body2"
            sx={{ color: NAVY, fontWeight: 700, flexShrink: 0 }}
        >
            {count}
        </Typography>
    </Stack>
);

const DoubleVoteTile: React.FC<{
    icon: React.ReactNode;
    label: string;
    tooltip: string;
    polls: { question: string; count: number }[];
}> = ({ icon, label, tooltip, polls }) => (
    <Paper
        data-pdf-section="true"
        elevation={0}
        sx={{
            p: { xs: 1.6, sm: 2 },
            borderRadius: 3,
            border: `1px solid ${BORDER}`,
            bgcolor: "#fff",
        }}
    >
        <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1.25 }}
        >
            <Box
                aria-hidden="true"
                sx={{
                    width: 29,
                    height: 29,
                    borderRadius: "9px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#edeffc",
                    color: INDIGO,
                    "& svg": { fontSize: 16 },
                }}
            >
                {icon}
            </Box>
            <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: NAVY }}
            >
                {label}
            </Typography>
            <Tooltip title={tooltip} arrow placement="top">
                <IconButton
                    aria-label={tooltip}
                    size="small"
                    sx={{ p: 0.25, color: BORDER }}
                >
                    <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
            </Tooltip>
        </Stack>

        <Stack spacing={1}>
            {polls.map((poll) => (
                <DoubleVotePollRow
                    key={poll.question}
                    question={poll.question}
                    count={poll.count}
                />
            ))}
        </Stack>
    </Paper>
);

interface TotalNumbersSectionProps {
    stats: ITotalNumbersStats;
}

const TotalNumbersSection: React.FC<TotalNumbersSectionProps> = ({ stats }) => {
    const { t } = useTranslation();
    const dash = "—";

    const respondentsValue =
        typeof stats.respondent_count === "number"
            ? String(stats.respondent_count)
            : dash;
    const totalVotesValue =
        typeof stats.total_votes === "number"
            ? String(stats.total_votes)
            : dash;
    const avgAnswersValue =
        typeof stats.avg_answers_per_respondent === "number"
            ? stats.avg_answers_per_respondent.toFixed(1)
            : dash;

    const highestEngagement = stats.highest_engagement_question;
    const completionPct =
        highestEngagement &&
        typeof highestEngagement.completion_rate === "number"
            ? Math.round(highestEngagement.completion_rate * 100)
            : null;
    const isNpsEngagement = highestEngagement?.poll_type === "nps";
    const engagementOptions = highestEngagement?.options ?? [];
    const doubleVotePolls = stats.double_vote_breakdown?.polls ?? [];

    return (
        <Box sx={{ mb: 3 }}>
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1.5 }}
            >
                <TrendingUpOutlinedIcon sx={{ fontSize: 18, color: INDIGO }} />
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 700,
                        color: NAVY,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        fontSize: "0.72rem",
                    }}
                >
                    {t("pages.eventInsights.report.totalNumbers.sectionTitle")}
                </Typography>
            </Stack>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                    gap: 2,
                    mb: 2,
                }}
            >
                <StatTile
                    icon={<GroupsOutlinedIcon />}
                    value={respondentsValue}
                    label={t(
                        "pages.eventInsights.report.totalNumbers.respondents"
                    )}
                    footer={
                        <SampleConfidenceChip tier={stats.sample_confidence} />
                    }
                />
                <StatTile
                    icon={<HowToVoteOutlinedIcon />}
                    value={totalVotesValue}
                    label={t(
                        "pages.eventInsights.report.totalNumbers.totalVotes"
                    )}
                />
                <StatTile
                    icon={<TrendingUpOutlinedIcon />}
                    value={avgAnswersValue}
                    label={t(
                        "pages.eventInsights.report.totalNumbers.avgAnswers"
                    )}
                />
            </Box>

            {doubleVotePolls.length > 0 ? (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                        },
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <DoubleVoteTile
                        icon={<HistoryOutlinedIcon />}
                        label={t(
                            "pages.eventInsights.report.totalNumbers.doubleVote.preVotes"
                        )}
                        tooltip={t(
                            "pages.eventInsights.report.totalNumbers.doubleVote.preVotesTooltip"
                        )}
                        polls={doubleVotePolls.map((poll) => ({
                            question: poll.question,
                            count: poll.pre_votes,
                        }))}
                    />
                    <DoubleVoteTile
                        icon={<TaskAltOutlinedIcon />}
                        label={t(
                            "pages.eventInsights.report.totalNumbers.doubleVote.postVotes"
                        )}
                        tooltip={t(
                            "pages.eventInsights.report.totalNumbers.doubleVote.postVotesTooltip"
                        )}
                        polls={doubleVotePolls.map((poll) => ({
                            question: poll.question,
                            count: poll.post_votes,
                        }))}
                    />
                </Box>
            ) : null}

            {highestEngagement ? (
                <Paper
                    data-pdf-section="true"
                    elevation={0}
                    sx={{
                        p: 2.5,
                        borderRadius: 3,
                        border: `1px solid ${BORDER}`,
                        bgcolor: "#fff",
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 1.25 }}
                    >
                        <Box
                            aria-hidden="true"
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "#edeffc",
                                color: INDIGO,
                                "& svg": { fontSize: 16 },
                            }}
                        >
                            <EmojiEventsOutlinedIcon />
                        </Box>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: NAVY }}
                        >
                            {t(
                                "pages.eventInsights.report.totalNumbers.highestEngagement"
                            )}
                        </Typography>
                        <Tooltip
                            title={t(
                                "pages.eventInsights.report.totalNumbers.highestEngagementTooltip"
                            )}
                            arrow
                            placement="top"
                        >
                            <IconButton
                                aria-label={t(
                                    "pages.eventInsights.report.totalNumbers.highestEngagementTooltip"
                                )}
                                size="small"
                                sx={{ p: 0.25, color: BORDER }}
                            >
                                <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <Box
                        sx={{
                            p: { xs: 1.5, sm: 2 },
                            borderRadius: 2,
                            bgcolor: "#fafbff",
                        }}
                    >
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ mb: 1.75 }}
                        >
                            <Typography
                                variant="body1"
                                sx={{ color: NAVY, fontWeight: 600 }}
                            >
                                {highestEngagement.question || dash}
                            </Typography>
                            {completionPct !== null ? (
                                <Chip
                                    size="small"
                                    label={t(
                                        "pages.eventInsights.report.totalNumbers.completionRate",
                                        { pct: completionPct }
                                    )}
                                    sx={{
                                        height: 22,
                                        fontSize: "0.68rem",
                                        fontWeight: 700,
                                        color: INDIGO,
                                        bgcolor: "#e6e8fc",
                                        flexShrink: 0,
                                        "& .MuiChip-label": { px: 1 },
                                    }}
                                />
                            ) : null}
                        </Stack>

                        {engagementOptions.length > 0 ? (
                            <Stack spacing={1}>
                                {engagementOptions.map((option) => (
                                    <EngagementOptionBar
                                        key={option.label}
                                        label={
                                            isNpsEngagement
                                                ? t(
                                                      `pages.eventInsights.report.totalNumbers.npsSegments.${option.label}`
                                                  )
                                                : option.label
                                        }
                                        count={option.count}
                                        percentage={option.percentage}
                                    />
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" sx={{ color: BORDER }}>
                                {t(
                                    "pages.eventInsights.report.totalNumbers.noData"
                                )}
                            </Typography>
                        )}
                    </Box>
                </Paper>
            ) : null}
        </Box>
    );
};

type ContentBlock =
    | { kind: "heading"; text: string }
    | { kind: "paragraph"; text: string }
    | { kind: "bulletList"; items: string[] };

/**
 * Heuristic heading detector — works regardless of exact LLM wording or casing.
 * A block's first line is treated as a heading when it:
 *   1. Is short (≤ 72 chars)
 *   2. Has ≤ 10 words
 *   3. Does not end with sentence-closing punctuation (., !, ?)
 *   4. Does not contain an embedded sentence break (". A…")
 */
const isLikelyHeading = (text: string): boolean => {
    const cleaned = text.trim().replace(/:$/, "");
    if (cleaned.length > 72) return false;
    if (/[.!?]$/.test(cleaned)) return false;
    if (/\.\s+[A-Z]/.test(cleaned)) return false;
    if (cleaned.split(/\s+/).length > 10) return false;
    return true;
};

/**
 * Keyword-based visualization section detector — matches any heading that
 * contains "visual" (e.g. "Visualization Recommendations", "Recommended
 * Visualizations", "Visual Summary", etc.), case-insensitive.
 */
const isVisualizationHeading = (text: string): boolean => {
    return text.trim().replace(/:$/, "").toLowerCase().includes("visual");
};

const parseReportBlocks = (text: string): ContentBlock[] => {
    const rawBlocks = text
        .split(/\n\n+/)
        .map((b) => b.trim())
        .filter(Boolean);

    const classified: ContentBlock[] = [];

    for (const block of rawBlocks) {
        const lines = block
            .split("\n")
            .map((l) => l.trim())
            .filter(Boolean);

        const allBullets = lines.every((l) => /^[•\-*]\s/.test(l));
        if (allBullets) {
            classified.push({
                kind: "bulletList",
                items: lines.map((l) => l.replace(/^[•\-*]\s+/, "").trim()),
            });
            continue;
        }

        const firstLine = lines[0]?.replace(/:$/, "") ?? "";

        if (isLikelyHeading(firstLine)) {
            classified.push({ kind: "heading", text: firstLine });

            const remainingLines = lines.slice(1);
            if (remainingLines.length > 0) {
                const remainingText = remainingLines.join(" ").trim();
                if (remainingText) {
                    classified.push({
                        kind: "paragraph",
                        text: remainingText,
                    });
                }
            }

            continue;
        }

        classified.push({ kind: "paragraph", text: block });
    }

    return classified.reduce<ContentBlock[]>((acc, block) => {
        const prev = acc[acc.length - 1];

        if (block.kind === "bulletList" && prev?.kind === "bulletList") {
            prev.items.push(...block.items);
        } else {
            acc.push(block);
        }

        return acc;
    }, []);
};

const CHART_COLORS = [
    INDIGO,
    "#7c3aed",
    "#0ea5e9",
    "#10b981",
    "#f59e0b",
    "#ef4444",
];

type ApexConfig = {
    series: ApexAxisChartSeries | ApexNonAxisChartSeries;
    options: ApexCharts.ApexOptions;
};

const sharedAxisBase = (chart: IVisualizationChart): ApexCharts.ApexOptions => {
    const datasets = chart.data.datasets ?? [];
    return {
        colors: CHART_COLORS,
        xaxis: {
            categories: chart.data.labels ?? [],
            labels: { style: { fontSize: "11px" }, trim: true, maxHeight: 60 },
        },
        yaxis: { labels: { style: { fontSize: "11px" } } },
        legend: { show: datasets.length > 1, fontSize: "12px" },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v: number) => String(v) } },
        grid: { borderColor: BORDER, strokeDashArray: 3 },
        chart: { toolbar: { show: false }, animations: { enabled: false } },
    };
};

const buildAxisSeries = (chart: IVisualizationChart): ApexAxisChartSeries =>
    (chart.data.datasets ?? []).map((d) => ({ name: d.label, data: d.values }));

const CHART_BUILDERS: Record<
    IVisualizationChart["type"],
    (chart: IVisualizationChart) => ApexConfig
> = {
    pie: (chart) => ({
        series: (chart.data.values ?? []) as ApexNonAxisChartSeries,
        options: {
            chart: {
                type: "pie",
                toolbar: { show: false },
                animations: { enabled: false },
            },
            labels: chart.data.labels ?? [],
            colors: CHART_COLORS,
            legend: { position: "bottom", fontSize: "12px" },
            dataLabels: { enabled: true, style: { fontSize: "11px" } },
            tooltip: { y: { formatter: (v: number) => String(v) } },
        },
    }),
    bar: (chart) => ({
        series: buildAxisSeries(chart),
        options: {
            ...sharedAxisBase(chart),
            chart: {
                ...sharedAxisBase(chart).chart,
                type: "bar",
                stacked: false,
            },
            plotOptions: {
                bar: {
                    borderRadius: 4,
                    columnWidth:
                        (chart.data.datasets ?? []).length > 1 ? "60%" : "45%",
                },
            },
        },
    }),
    grouped_bar: (chart) => ({
        series: buildAxisSeries(chart),
        options: {
            ...sharedAxisBase(chart),
            chart: {
                ...sharedAxisBase(chart).chart,
                type: "bar",
                stacked: false,
            },
            plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
        },
    }),
    stacked_bar: (chart) => ({
        series: buildAxisSeries(chart),
        options: {
            ...sharedAxisBase(chart),
            chart: {
                ...sharedAxisBase(chart).chart,
                type: "bar",
                stacked: true,
            },
            plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
        },
    }),
    line: (chart) => ({
        series: buildAxisSeries(chart),
        options: {
            ...sharedAxisBase(chart),
            chart: { ...sharedAxisBase(chart).chart, type: "line" },
            stroke: { curve: "smooth", width: 2 },
        },
    }),
};

const buildApexOptions = (chart: IVisualizationChart): ApexConfig => {
    const builder = CHART_BUILDERS[chart.type] ?? CHART_BUILDERS.bar;
    return builder(chart);
};

const CHART_ICON_MAP: Record<IVisualizationChart["type"], React.ReactNode> = {
    bar: <BarChartIcon fontSize="small" />,
    grouped_bar: <BarChartIcon fontSize="small" />,
    stacked_bar: <BarChartIcon fontSize="small" />,
    pie: <PieChartIcon fontSize="small" />,
    line: <ShowChartIcon fontSize="small" />,
};

const VizChartCard: React.FC<{ chart: IVisualizationChart }> = ({ chart }) => {
    const { t } = useTranslation();
    const { series, options } = buildApexOptions(chart);
    const isPie = chart.type === "pie";

    return (
        <Paper
            data-pdf-section="true"
            elevation={0}
            sx={{
                p: { xs: 2.5, md: 3 },
                height: { xs: "auto", md: 500 },
                minHeight: 460,
                borderRadius: 3,
                border: `1px solid ${BORDER}`,
                bgcolor: "#fff",
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                pageBreakInside: "avoid",
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box
                    sx={{
                        color: INDIGO,
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    {CHART_ICON_MAP[chart.type] ?? (
                        <BarChartIcon fontSize="small" />
                    )}
                </Box>

                <Typography variant="subtitle2" fontWeight={700} color={NAVY}>
                    {chart.title}
                </Typography>
            </Stack>

            {chart.description && (
                <Typography
                    variant="body2"
                    sx={{
                        color: "#475569",
                        lineHeight: 1.65,
                        textAlign: "center",
                    }}
                >
                    {chart.description}
                </Typography>
            )}

            <Box sx={{ mt: 0.5, flex: 1, minHeight: 300 }}>
                <ReactApexChart
                    type={
                        isPie ? "pie" : chart.type === "line" ? "line" : "bar"
                    }
                    series={series as any}
                    options={options}
                    height={330}
                />
            </Box>

            <Box
                sx={{
                    mt: "auto",
                    pt: 1.5,
                    borderTop: `1px dashed ${BORDER}`,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: MUTED,
                        lineHeight: 1.65,
                        display: "block",
                    }}
                >
                    <Box component="span" fontWeight={700}>
                        {t("pages.eventInsights.report.whyItMatters")}{" "}
                    </Box>
                    {chart.why_it_matters}
                </Typography>
            </Box>
        </Paper>
    );
};

const VisualizationInlineSection: React.FC<{
    vizCharts: IVisualizationChart[];
    vizLoading: boolean;
}> = ({ vizCharts, vizLoading }) => {
    const { t } = useTranslation();

    if (vizLoading) {
        return (
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ py: 3 }}
            >
                <CircularProgress size={18} sx={{ color: INDIGO }} />
                <Typography variant="body2" color={MUTED}>
                    {t("pages.eventInsights.report.vizLoading")}
                </Typography>
            </Stack>
        );
    }

    if (vizCharts.length === 0) {
        return (
            <Typography
                variant="body2"
                sx={{
                    color: MUTED,
                    fontStyle: "italic",
                    mt: 1,
                }}
            >
                {t("pages.eventInsights.report.vizEmpty")}
            </Typography>
        );
    }

    return (
        <Grid container spacing={3} sx={{ mt: 1 }}>
            {vizCharts.map((chart, index) => (
                <Grid item xs={12} key={(chart as any).id ?? index}>
                    <VizChartCard chart={chart} />
                </Grid>
            ))}
        </Grid>
    );
};

type ReportSection = {
    id: string;
    heading?: string;
    blocks: Exclude<ContentBlock, { kind: "heading" }>[];
    isVisualization: boolean;
};

const buildReportSections = (
    contentBlocks: ContentBlock[]
): ReportSection[] => {
    const sections: ReportSection[] = [];
    let currentSection: ReportSection | null = null;
    let skipVisualizationContent = false;

    const pushCurrentSection = () => {
        if (!currentSection) return;

        if (
            currentSection.heading ||
            currentSection.blocks.length > 0 ||
            currentSection.isVisualization
        ) {
            sections.push(currentSection);
        }

        currentSection = null;
    };

    contentBlocks.forEach((block, index) => {
        if (block.kind === "heading") {
            pushCurrentSection();

            const isVisualization = isVisualizationHeading(block.text);
            currentSection = {
                id: `section-${index}`,
                heading: block.text,
                blocks: [],
                isVisualization,
            };
            skipVisualizationContent = isVisualization;
            return;
        }

        if (skipVisualizationContent) {
            return;
        }

        if (!currentSection) {
            currentSection = {
                id: `section-${index}`,
                blocks: [],
                isVisualization: false,
            };
        }

        currentSection.blocks.push(block);
    });

    pushCurrentSection();

    return sections;
};

const ExecutiveSummarySection: React.FC<{
    text: string;
    vizCharts: IVisualizationChart[];
    vizLoading: boolean;
}> = ({ text, vizCharts, vizLoading }) => {
    const contentBlocks = parseReportBlocks(text);
    const sections = buildReportSections(contentBlocks);

    return (
        <Paper
            data-pdf-section="true"
            elevation={0}
            sx={{
                p: { xs: 3, md: 4 },
                mb: 3,
                borderRadius: 3,
                border: `1px solid ${BORDER}`,
                bgcolor: "#fff",
                pageBreakInside: "avoid",
            }}
        >
            <Box>
                {sections.map((section, sectionIndex) => {
                    const hasHeading = Boolean(section.heading);

                    return (
                        <Box
                            key={section.id}
                            data-pdf-section="true"
                            data-pdf-protect={hasHeading ? "true" : undefined}
                            sx={{
                                mt: sectionIndex === 0 ? 0.5 : 2.5,
                                mb: section.isVisualization ? 3 : 1,
                            }}
                        >
                            {section.heading && (
                                <Typography
                                    variant="body2"
                                    data-pdf-protect="true"
                                    sx={{
                                        ...ReportSectionTitleSx,
                                        mb: section.isVisualization ? 2 : 0.75,
                                    }}
                                >
                                    {section.heading}
                                </Typography>
                            )}

                            {section.isVisualization ? (
                                <VisualizationInlineSection
                                    vizCharts={vizCharts}
                                    vizLoading={vizLoading}
                                />
                            ) : (
                                section.blocks.map((block, blockIndex) => {
                                    if (block.kind === "bulletList") {
                                        return (
                                            <List
                                                key={`${section.id}-list-${blockIndex}`}
                                                dense
                                                disablePadding
                                                sx={{ mb: 0.5 }}
                                            >
                                                {block.items.map(
                                                    (item, itemIndex) => (
                                                        <ListItem
                                                            key={`${section.id}-item-${itemIndex}`}
                                                            disableGutters
                                                            alignItems="flex-start"
                                                            sx={{
                                                                py: "3px",
                                                                pl: 0,
                                                            }}
                                                        >
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    flexShrink: 0,
                                                                    width: 18,
                                                                    pt: "1px",
                                                                    color: INDIGO,
                                                                    fontSize:
                                                                        "0.45rem",
                                                                    lineHeight:
                                                                        "1.75",
                                                                }}
                                                            >
                                                                ●
                                                            </Box>

                                                            <ListItemText
                                                                primary={item}
                                                                primaryTypographyProps={{
                                                                    variant:
                                                                        "body2",
                                                                    sx: {
                                                                        color: "#475569",
                                                                        lineHeight: 1.55,
                                                                        m: 0,
                                                                    },
                                                                }}
                                                                sx={{ m: 0 }}
                                                            />
                                                        </ListItem>
                                                    )
                                                )}
                                            </List>
                                        );
                                    }

                                    return (
                                        <Typography
                                            key={`${section.id}-paragraph-${blockIndex}`}
                                            variant="body2"
                                            sx={{
                                                lineHeight: 1.7,
                                                color: "#475569",
                                                textAlign: "left",
                                                maxWidth: 760,
                                                mb: 1.5,
                                                mt: blockIndex === 0 ? 0 : 0.5,
                                            }}
                                        >
                                            {block.text}
                                        </Typography>
                                    );
                                })
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
};

interface ReportStateCardProps {
    heading: string;
    body: string;
    loading?: boolean;
    animationData?: object;
}

const ReportStateCard: React.FC<ReportStateCardProps> = ({
    heading,
    body,
    loading = false,
    animationData,
}) => {
    let icon = <AutoAwesomeIcon sx={{ fontSize: 44, color: BORDER, mb: 2 }} />;

    if (loading) {
        icon = animationData ? (
            <Box sx={{ width: "100%", maxWidth: 280, mx: "auto", mb: 1.5 }}>
                <React.Suspense
                    fallback={<CircularProgress sx={{ color: INDIGO }} />}
                >
                    <Lottie
                        animationData={animationData}
                        loop
                        style={{ width: "100%", height: "100%" }}
                    />
                </React.Suspense>
            </Box>
        ) : (
            <CircularProgress sx={{ color: INDIGO, mb: 2 }} />
        );
    }

    return (
        <Paper
            data-pdf-section="true"
            elevation={0}
            sx={{
                p: 7,
                mb: 3,
                textAlign: "center",
                borderRadius: 3,
                border: `1px dashed ${BORDER}`,
                bgcolor: "#fafbff",
            }}
        >
            {icon}

            <Typography variant="h6" fontWeight={700} color={NAVY} gutterBottom>
                {heading}
            </Typography>

            <Typography variant="body2" color={MUTED}>
                {body}
            </Typography>
        </Paper>
    );
};

const EmptyState: React.FC = () => {
    const { t } = useTranslation();
    return (
        <ReportStateCard
            heading={t("pages.eventInsights.report.emptyHeading")}
            body={t("pages.eventInsights.report.noReportBody")}
        />
    );
};

const GeneratingState: React.FC = () => {
    const { t } = useTranslation();
    const [animationData, setAnimationData] = useState<object | null>(null);

    useEffect(() => {
        let mounted = true;

        import("./animations/reportGenerating.json").then((module) => {
            if (mounted) {
                setAnimationData(module.default);
            }
        });

        return () => {
            mounted = false;
        };
    }, []);

    return (
        <ReportStateCard
            loading
            animationData={animationData ?? undefined}
            heading={t("pages.eventInsights.report.generatingHeading")}
            body={t("pages.eventInsights.report.emptyBody")}
        />
    );
};

interface EventReportTabProps {
    channel: IChannel;
}

export const EventReportTab: React.FC<EventReportTabProps> = ({ channel }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const reportRef = useRef<HTMLDivElement>(null);
    const isReportReady = Boolean(channel.executive_summary?.trim());
    const isReportGenerating = Boolean(channel.closed) && !isReportReady;
    const [downloading, setDownloading] = useState(false);
    const [reportGeneratedAt, setReportGeneratedAt] = useState<string | null>(
        null
    );
    const [vizCharts, setVizCharts] = useState<IVisualizationChart[]>(
        channel.visualization_charts ?? []
    );
    const [vizLoading, setVizLoading] = useState(false);
    const [totalNumbersStats, setTotalNumbersStats] =
        useState<ITotalNumbersStats | null>(
            channel.total_numbers_stats ?? null
        );

    useEffect(() => {
        const requestChannelId = channel._id;

        if (!requestChannelId || !isReportReady) {
            setReportGeneratedAt(null);
            setVizCharts(channel.visualization_charts ?? []);
            setTotalNumbersStats(channel.total_numbers_stats ?? null);
            setVizLoading(false);
            return;
        }

        setVizCharts(channel.visualization_charts ?? []);
        setTotalNumbersStats(channel.total_numbers_stats ?? null);

        if ((channel.visualization_charts ?? []).length > 0) {
            setVizLoading(false);
        } else {
            setVizLoading(true);
        }

        let stale = false;

        dispatch(ChannelActions.fetchChannelReportData(requestChannelId))
            .then(
                ({
                    reportGeneratedAt,
                    vizCharts: charts,
                    totalNumbersStats: stats,
                }) => {
                    if (stale) return;
                    setReportGeneratedAt(reportGeneratedAt);
                    setVizCharts(charts);
                    setTotalNumbersStats(stats);
                }
            )
            .catch(() => {
                if (stale) return;
                setVizCharts(channel.visualization_charts ?? []);
                setTotalNumbersStats(channel.total_numbers_stats ?? null);
            })
            .finally(() => {
                if (stale) return;
                setVizLoading(false);
            });

        return () => {
            stale = true;
        };
    }, [
        channel._id,
        isReportReady,
        channel.visualization_charts,
        channel.total_numbers_stats,
        dispatch,
    ]);

    useEffect(() => {
        if (!channel._id || !isReportGenerating) return;

        let mounted = true;
        const refreshChannel = async () => {
            try {
                await dispatch(ChannelActions.fetchChannelById(channel._id));
            } catch {
                // Keep polling; transient errors are handled by next interval.
            }
        };

        refreshChannel();

        const intervalId = window.setInterval(() => {
            if (!mounted) return;
            refreshChannel();
        }, REPORT_POLL_INTERVAL_MS);

        return () => {
            mounted = false;
            window.clearInterval(intervalId);
        };
    }, [channel._id, dispatch, isReportGenerating]);

    const handlePrint = useCallback(async () => {
        if (!reportRef.current) return;

        setDownloading(true);
        try {
            const sectionBreakpoints = collectSectionBreakpoints(
                reportRef.current,
                PDF_CAPTURE_SCALE
            );
            const protectedRanges = collectProtectedRanges(
                reportRef.current,
                PDF_CAPTURE_SCALE
            );
            const canvas = await html2canvas(reportRef.current, {
                backgroundColor: "#ffffff",
                scale: PDF_CAPTURE_SCALE,
                useCORS: true,
                windowWidth: Math.max(
                    reportRef.current.scrollWidth,
                    reportRef.current.clientWidth
                ),
                ignoreElements: (element) =>
                    element.classList?.contains("no-print") ?? false,
            });

            const pdf = await PDFDocument.create();
            const sourceWidth = canvas.width;
            const sourceHeight = canvas.height;
            const renderedWidth = PDF_PAGE_WIDTH;
            const renderedPageHeightInSourcePx = Math.floor(
                (PDF_PAGE_HEIGHT * sourceWidth) / renderedWidth
            );
            const minTrailingPageHeightInSourcePx = Math.floor(
                renderedPageHeightInSourcePx * MIN_TRAILING_PAGE_FILL_RATIO
            );

            let sourceYOffset = 0;

            while (sourceYOffset < sourceHeight) {
                const sliceHeight = Math.min(
                    renderedPageHeightInSourcePx,
                    sourceHeight - sourceYOffset
                );
                const maxSliceEnd = sourceYOffset + sliceHeight;
                const minPreferredSliceEnd =
                    sourceYOffset +
                    Math.floor(
                        renderedPageHeightInSourcePx * MIN_PAGE_FILL_RATIO
                    );
                const minFallbackSliceEnd =
                    sourceYOffset +
                    Math.floor(
                        renderedPageHeightInSourcePx *
                            FALLBACK_BREAK_MIN_FILL_RATIO
                    );

                // Prefer section boundaries to avoid splitting cards/charts across pages.
                const preferredBreak = getLatestBreakpointInRange(
                    sectionBreakpoints,
                    sourceYOffset,
                    maxSliceEnd,
                    minPreferredSliceEnd
                );

                // If no breakpoint satisfies the preferred fill ratio,
                // still break at the latest available section boundary to avoid splitting blocks.
                const fallbackSectionBreak = getLatestBreakpointInRange(
                    sectionBreakpoints,
                    sourceYOffset,
                    maxSliceEnd,
                    sourceYOffset + 1
                );

                const breakCandidate =
                    preferredBreak ??
                    (fallbackSectionBreak &&
                    fallbackSectionBreak >= minFallbackSliceEnd
                        ? fallbackSectionBreak
                        : null);
                const remainingAfterCandidate = breakCandidate
                    ? sourceHeight - breakCandidate
                    : sourceHeight - maxSliceEnd;
                const wouldCreateTinyTrailingPage =
                    remainingAfterCandidate > 0 &&
                    remainingAfterCandidate < minTrailingPageHeightInSourcePx;
                const resolvedSliceEnd =
                    breakCandidate && !wouldCreateTinyTrailingPage
                        ? breakCandidate
                        : maxSliceEnd;
                const overlappingProtectedRange = protectedRanges.find(
                    (range) =>
                        resolvedSliceEnd >=
                            range.start - PROTECTED_BREAK_TOLERANCE_PX &&
                        resolvedSliceEnd <=
                            range.end + PROTECTED_BREAK_TOLERANCE_PX
                );
                const adjustedSliceEnd =
                    overlappingProtectedRange &&
                    overlappingProtectedRange.start > sourceYOffset + 200
                        ? overlappingProtectedRange.start
                        : resolvedSliceEnd;
                const resolvedSliceHeight = Math.max(
                    200,
                    adjustedSliceEnd - sourceYOffset
                );

                const pageCanvas = document.createElement("canvas");
                pageCanvas.width = sourceWidth;
                pageCanvas.height = resolvedSliceHeight;
                const ctx = pageCanvas.getContext("2d");

                if (!ctx) {
                    throw new Error("Failed to initialize canvas context.");
                }

                ctx.drawImage(
                    canvas,
                    0,
                    sourceYOffset,
                    sourceWidth,
                    resolvedSliceHeight,
                    0,
                    0,
                    sourceWidth,
                    resolvedSliceHeight
                );

                const pagePng = await pdf.embedPng(
                    pageCanvas.toDataURL("image/png")
                );
                const page = pdf.addPage([PDF_PAGE_WIDTH, PDF_PAGE_HEIGHT]);
                const pageRenderedHeight =
                    (resolvedSliceHeight * renderedWidth) / sourceWidth;

                page.drawImage(pagePng, {
                    x: 0,
                    y: PDF_PAGE_HEIGHT - pageRenderedHeight,
                    width: renderedWidth,
                    height: pageRenderedHeight,
                });

                sourceYOffset += resolvedSliceHeight;
            }

            const pdfBytes = await pdf.save();
            const blob = new Blob([pdfBytes], { type: "application/pdf" });
            const fileName = sanitizeFileName(
                channel.title || `channel_${channel._id || "report"}`
            );
            downloadBlob(blob, `${fileName}_report.pdf`);
        } catch (error) {
            dispatch(
                NotificationActions.notification({
                    snackbarOpen: true,
                    snackbarType: "error",
                    snackbarMessage: t("snackbar_fail"),
                })
            );
        } finally {
            setDownloading(false);
        }
    }, [channel._id, channel.title, dispatch, t]);

    const handleDownloadCsv = useCallback(async () => {
        if (!channel._id) return;

        setDownloading(true);

        try {
            const blob = await dispatch(
                PollActions.exportChannelRawVotes(channel._id)
            );
            downloadBlob(blob, `channel-${channel._id}-raw-votes.csv`);
        } finally {
            setDownloading(false);
        }
    }, [channel._id, dispatch]);

    const handleDownloadJson = useCallback(async () => {
        if (!channel._id) return;

        setDownloading(true);

        try {
            const blob = await dispatch(
                PollActions.exportChannelRawVotes(channel._id, "json")
            );
            downloadBlob(blob, `channel-${channel._id}-raw-votes.json`);
        } finally {
            setDownloading(false);
        }
    }, [channel._id, dispatch]);

    return (
        <Box sx={{ mt: 2, mb: 8 }}>
            <Box ref={reportRef}>
                <ReportHero
                    generatedAt={reportGeneratedAt}
                    downloading={downloading}
                    onDownloadCsv={handleDownloadCsv}
                    onDownloadJson={handleDownloadJson}
                    onPrint={handlePrint}
                />

                {isReportReady ? (
                    <>
                        {totalNumbersStats ? (
                            <TotalNumbersSection stats={totalNumbersStats} />
                        ) : null}

                        <ExecutiveSummarySection
                            text={channel.executive_summary ?? ""}
                            vizCharts={vizCharts}
                            vizLoading={vizLoading}
                        />
                    </>
                ) : isReportGenerating ? (
                    <GeneratingState />
                ) : (
                    <EmptyState />
                )}
            </Box>

            <style>{`
                @media print {
                    .no-print { display: none !important; }
                }
            `}</style>
        </Box>
    );
};
